import { Injectable, inject, signal } from '@angular/core';
import { type ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { BackendTranslationService } from './backend.translation.service';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private translate = inject(TranslateService);
  private backendTranslationService = inject(BackendTranslationService);

  currentLang = 'en';

  private languageChangedSignal = signal<string>('');
  readonly languageChanged = this.languageChangedSignal.asReadonly();

  constructor() {
    const storedLang = localStorage.getItem('lang') || 'en';
    this.currentLang = storedLang;

    this.translate.setDefaultLang(this.currentLang);
    this.translate.use(this.currentLang);
  }

  async init(): Promise<void> {
    await this.backendTranslationService.init(this.currentLang);

    // preload shared scopes ONCE
    const eagerScopes = this.backendTranslationService.getEagerScopes();

    await Promise.all(eagerScopes.map(scope => this.backendTranslationService.loadScope(this.currentLang, scope)));
  }

  async setLanguage(lang: string): Promise<void> {
    if (lang === this.currentLang) {
      return;
    }

    const oldLang = this.currentLang;

    this.currentLang = lang;
    localStorage.setItem('lang', lang);
    this.translate.use(lang);

    this.backendTranslationService.clearLanguage(oldLang);
    await this.backendTranslationService.init(this.currentLang);

    const eagerScopes = this.backendTranslationService.getEagerScopes();

    await Promise.all(eagerScopes.map(scope => this.backendTranslationService.loadScope(lang, scope)));

    this.languageChangedSignal.set(lang);
  }

  async reloadActiveRouteScope(route: ActivatedRoute): Promise<void> {
    let active = route;
    while (active.firstChild) {
      active = active.firstChild;
    }

    const scope = active.snapshot.data['translationScope'];
    if (scope) {
      await this.backendTranslationService.loadScope(this.currentLang, scope);
    }
  }
}
